// Run station root: the session loop (Phase 4).
//
// Thin React shell over the server-authoritative loop. Decision logic lives in
// session/runner.ts (pure, unit-tested); this wires it into screen state:
//
//   parse the study URL -> show a landing screen -> on Begin, create the
//   session -> poll the loading screen until the server builds it -> hand each
//   step to its renderer -> on the renderer's result, submit and let the server
//   advance -> repeat -> redirect back to the platform on completion (or to the
//   return path if consent is declined).
//
// The chat block (renderer added in 4.2b) reports "advanced" instead of a
// result, because its streaming endpoint advances the session server-side; we
// then re-sync to the new step rather than submitting.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  parseLaunchParams,
  pollUntilReady,
  interpretSubmit,
  resolveCompletion,
  type StepInterpretation,
  type PollResult,
} from "./session/runner";
import { submitMturkForm } from "./session/mturk";
import { useIdleWatcher } from "./session/idleWatcher";
import { saveResume, loadResume, clearResume } from "./session/resumeStore";
import { setSessionContext } from "./sentry";
import {
  createSession,
  fetchStep,
  fetchExperimentBegin,
  submitStep,
  sendCloseBeacon,
  type SessionHandle,
} from "./session/sessionDriver";
import { RENDERERS, type RendererOutcome } from "./renderers/registry";
import { SessionApiError } from "./api/client";
import type { Identity, IdentitySource, StepEnvelope, StepType, StepResult } from "./api/types";
import { Screen, Card, Message, Loading, Button } from "./ui/primitives";

type Phase =
  | { name: "boot" }
  // The real participant's first screen. Carries the study's welcome text +
  // button label (fetched before the session exists) so the participant sees
  // the researcher's welcome, not a generic one. Begin creates the session.
  | { name: "landing"; welcomeHtml?: string; buttonLabel?: string }
  // Owner sandbox preview: the SAME landing screen, but Begin drives the
  // pre-built preview session. Renders from the same welcome source as the real
  // landing so the preview shows exactly what the participant will see.
  | { name: "previewLanding"; welcomeHtml: string; buttonLabel: string }
  | { name: "preview" }
  | { name: "loading"; message: string }
  | { name: "running"; step: StepEnvelope; stepIndex: number }
  | { name: "redirecting"; url: string; code?: string | null }
  | { name: "submitting"; submitTo: string; assignmentId: string }
  | { name: "mturkError"; assignmentId: string | null }
  // tone:"complete" is the success screen. tone:"aborted" is the platform- and
  // reason-aware end screen (see AbortedScreen); endReason/platform are read only
  // for the aborted tone and ignored for complete.
  | { name: "ended"; tone: "complete" | "aborted"; endReason?: string | null; platform?: IdentitySource }
  // A retryable submit failure: unlike "error" (a static dead-end), this screen
  // offers "Try again", which re-submits the retained result at the same step_index.
  | { name: "submitError"; message: string }
  | { name: "error"; message: string };

function errorMessage(err: unknown): string {
  if (err instanceof SessionApiError && err.message) return err.message;
  return "Something went wrong. Please refresh to try again.";
}

export default function App() {
  const [phase, setPhase] = useState<Phase>({ name: "boot" });
  const [isSandbox, setIsSandbox] = useState(false);
  // Set only if window.location.assign() actually throws, so the redirect screen
  // can switch from the reassuring "Returning you..." copy to an explicit failure.
  const [redirectFailed, setRedirectFailed] = useState(false);
  const paramsRef = useRef<{ experimentId: string; identity: Identity } | null>(null);
  const handleRef = useRef<SessionHandle | null>(null);
  // The pre-built sandbox session, stashed while the owner is on the preview
  // landing screen so Begin can drive it.
  const sandboxHandleRef = useRef<SessionHandle | null>(null);
  const ctxRef = useRef<{ step: StepEnvelope; stepIndex: number } | null>(null);
  // The MTurk form POST navigates away; guard so the effect can never fire it
  // twice (a future re-entry or StrictMode-style double-invoke can't double-POST).
  const mturkSubmittedRef = useRef(false);
  // The just-completed block's submit, retained so a failed submit is retryable
  // (the renderer that produced the result has already unmounted). Cleared on
  // success or on a 409 already-applied recovery.
  const pendingSubmitRef = useRef<{ stepIndex: number; type: StepType; result: StepResult } | null>(
    null,
  );

  const goRunning = useCallback((step: StepEnvelope, stepIndex: number) => {
    ctxRef.current = { step, stepIndex };
    setPhase({ name: "running", step, stepIndex });
  }, []);

  const applyStepInterp = useCallback(
    (interp: StepInterpretation) => {
      if (interp.kind === "ready") goRunning(interp.step, interp.stepIndex);
      else if (interp.kind === "ended") {
        clearResume(); // a finished/declined session must never be resumed
        setPhase(
          interp.completed
            ? { name: "ended", tone: "complete" }
            : {
                name: "ended",
                tone: "aborted",
                endReason: interp.endReason ?? null,
                platform: paramsRef.current?.identity.source ?? "anonymous",
              },
        );
      } else setPhase({ name: "error", message: "Lost track of the study. Please refresh." });
    },
    [goRunning],
  );

  // Route a pollUntilReady result to the next screen, preserving FE-1's
  // reason-aware end handling (end_reason flows through applyStepInterp).
  const applyPollResult = useCallback(
    (result: PollResult) => {
      if (result.kind === "timeout")
        setPhase({
          name: "error",
          message: "The study is taking too long to continue. Please refresh to try again.",
        });
      else
        applyStepInterp(
          result.kind === "ready"
            ? { kind: "ready", step: result.step, stepIndex: result.stepIndex }
            : {
                kind: "ended",
                completed: result.completed,
                aborted: result.aborted,
                ...(result.endReason != null ? { endReason: result.endReason } : {}),
              },
        );
    },
    [applyStepInterp],
  );

  // Re-sync to the server's current step by polling GET /step. Shared by the chat
  // advance path (1d) and the 409 already-applied recovery (1e Part 1). The short
  // budget resolves on attempt 1 in the normal case (no added latency); it never
  // throws — a fetch failure routes to the error screen.
  const resync = useCallback(
    async (handle: SessionHandle) => {
      try {
        const result = await pollUntilReady(() => fetchStep(handle), {
          intervalMs: 800,
          maxAttempts: 8,
        });
        applyPollResult(result);
      } catch (err) {
        setPhase({ name: "error", message: errorMessage(err) });
      }
    },
    [applyPollResult],
  );

  // Sandbox preview: the owner opened a pre-built preview session. Skip the
  // landing screen and createSession; drive the existing session directly.
  const beginSandbox = useCallback(
    async (handle: SessionHandle) => {
      handleRef.current = handle;
      setPhase({ name: "loading", message: "Loading preview..." });
      try {
        const result = await pollUntilReady(() => fetchStep(handle));
        if (result.kind === "ready") goRunning(result.step, result.stepIndex);
        else if (result.kind === "ended")
          setPhase(
            result.completed
              ? { name: "ended", tone: "complete" }
              : {
                  name: "ended",
                  tone: "aborted",
                  endReason: null,
                  platform: paramsRef.current?.identity.source ?? "anonymous",
                },
          );
        else
          setPhase({
            name: "error",
            message: "The preview is taking too long to start. Please refresh to try again.",
          });
      } catch (err) {
        setPhase({ name: "error", message: errorMessage(err) });
      }
    },
    [goRunning],
  );

  // Begin from the preview landing screen: drive the stashed sandbox session.
  const beginSandboxPreview = useCallback(() => {
    if (sandboxHandleRef.current) void beginSandbox(sandboxHandleRef.current);
  }, [beginSandbox]);

  // Resume-on-reload (2c): rehydrate a stored handle and re-sync to its current
  // step, reusing begin's pollUntilReady machinery (lands on running -> the 2a
  // idle watcher arms, since the stored handle carries idleTimeoutSeconds).
  const resumeRun = useCallback(
    async (handle: SessionHandle) => {
      setPhase({ name: "loading", message: "Resuming your session..." });
      // Tag crash reports for the resumed run (NON-PII identifiers only).
      const resumeParams = paramsRef.current;
      setSessionContext({
        sessionId: handle.sessionId,
        ...(resumeParams
          ? { experimentId: resumeParams.experimentId, source: resumeParams.identity.source }
          : {}),
      });
      try {
        const result = await pollUntilReady(() => fetchStep(handle));
        if (result.kind === "ended") clearResume(); // already finished: don't resume again
        applyPollResult(result); // ready -> running; ended -> FE-1 screen; timeout -> error
      } catch (err) {
        // A dead/stale stored handle (bad token / session gone) must not dead-end:
        // clear it and fall back to the normal landing/begin path (start clean).
        if (
          err instanceof SessionApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          clearResume();
          handleRef.current = null;
          setPhase({ name: "landing" });
          return;
        }
        setPhase({ name: "error", message: errorMessage(err) });
      }
    },
    [applyPollResult],
  );

  // Parse the URL once; then wait on a landing screen for the participant to begin.
  useEffect(() => {
    const { experimentId, identity, preview, sandbox } = parseLaunchParams(window.location.search);
    if (!experimentId) {
      setPhase({ name: "error", message: "This study link is missing its study id." });
      return;
    }
    paramsRef.current = { experimentId, identity };

    // Fetch the study's welcome text + button label so the FIRST screen shows
    // the researcher's welcome (not a generic one). Public, before any session
    // exists. Both the preview and the real landing call this, so they render
    // identically - the preview shows exactly what the participant will see.
    const loadBegin = async (): Promise<{ welcomeHtml: string; buttonLabel: string }> => {
      try {
        const info = await fetchExperimentBegin(experimentId);
        return {
          welcomeHtml: info.welcome_text || "",
          buttonLabel: info.begin_button_label || "Begin",
        };
      } catch {
        return { welcomeHtml: "", buttonLabel: "Begin" };
      }
    };

    if (sandbox) {
      // Researcher sandbox: show the Begin/landing screen FIRST (so the owner
      // sees the participant's welcome message and the Begin gate), then drive
      // the pre-built preview session on Begin. Sandbox is never persisted or resumed.
      setIsSandbox(true);
      sandboxHandleRef.current = { sessionId: sandbox.sessionId, sessionToken: sandbox.sessionToken };
      void loadBegin().then(({ welcomeHtml, buttonLabel }) =>
        setPhase({ name: "previewLanding", welcomeHtml, buttonLabel }));
      return;
    }
    // MTurk preview: the worker hasn't accepted the HIT, so don't start (or resume).
    if (preview) {
      setPhase({ name: "preview" });
      return;
    }
    // Resume-on-reload: if this tab holds a session for this exact launch (same
    // experiment + source + external_id; anonymous matches on experiment+source),
    // rehydrate and continue where they left off instead of showing landing.
    const resumed = loadResume(experimentId, {
      source: identity.source,
      externalId: identity.external_id ?? "",
    });
    if (resumed) {
      handleRef.current = resumed;
      void resumeRun(resumed);
      return;
    }
    // Real participant: fetch the welcome, then show the landing with it.
    void loadBegin().then(({ welcomeHtml, buttonLabel }) =>
      setPhase({ name: "landing", welcomeHtml, buttonLabel }));
  }, [beginSandbox, resumeRun]);

  // Perform the hand-off when we enter the redirecting or submitting phase.
  // Both calls are wrapped so a throw can't crash the effect and tear down the
  // screen — on failure the screen stays mounted with the recourse text (the
  // completion code for redirect, the assignment id for MTurk).
  useEffect(() => {
    if (phase.name === "redirecting") {
      try {
        // Scheme guard (defense-in-depth): phase.url is backend-sourced today, but
        // only ever navigate to an http(s) URL so a non-http(s) scheme can't run.
        const u = new URL(phase.url, window.location.origin);
        if (u.protocol === "http:" || u.protocol === "https:") {
          window.location.assign(phase.url);
        } else {
          setRedirectFailed(true);
        }
      } catch {
        // malformed url: do not navigate; show the explicit failure message.
        setRedirectFailed(true);
      }
    } else if (phase.name === "submitting") {
      if (mturkSubmittedRef.current) return; // POST exactly once per completion.
      mturkSubmittedRef.current = true;
      try {
        submitMturkForm(phase.submitTo, phase.assignmentId);
      } catch {
        // Stay on the submitting screen; its recourse text is the fallback.
      }
    }
  }, [phase]);

  const begin = useCallback(async () => {
    const params = paramsRef.current;
    if (!params) return;
    setPhase({ name: "loading", message: "Preparing your session..." });
    try {
      const handle = await createSession(params.experimentId, params.identity);
      handleRef.current = handle;
      // Persist so a reload resumes this session instead of minting a new one.
      // (sandbox never reaches here; resumeStore also ignores sandbox defensively.)
      saveResume(
        params.experimentId,
        { source: params.identity.source, externalId: params.identity.external_id ?? "" },
        handle,
      );
      // Tag crash reports with NON-PII run identifiers (no token, no participant id).
      setSessionContext({
        sessionId: handle.sessionId,
        experimentId: params.experimentId,
        source: params.identity.source,
      });
      const result = await pollUntilReady(() => fetchStep(handle));
      if (result.kind === "ready") goRunning(result.step, result.stepIndex);
      else if (result.kind === "ended")
        setPhase({ name: "ended", tone: result.completed ? "complete" : "aborted" });
      else
        setPhase({
          name: "error",
          message: "The study is taking too long to start. Please refresh to try again.",
        });
    } catch (err) {
      setPhase({ name: "error", message: errorMessage(err) });
    }
  }, [goRunning]);

  // The one submit code path, shared by the initial submit and the retry so they
  // can't drift. step_index is passed explicitly (never re-derived) so a retry
  // re-submits the SAME index — the server's 409 guard makes that safe.
  const runSubmit = useCallback(
    async (handle: SessionHandle, stepIndex: number, type: StepType, result: StepResult) => {
      setPhase({ name: "loading", message: "Saving..." });
      try {
        const resp = await submitStep(handle, stepIndex, type, result);
        const decided = interpretSubmit(resp);
        switch (decided.kind) {
          case "next":
            goRunning(decided.step, decided.stepIndex);
            break;
          case "complete": {
            clearResume(); // terminal: never resume a completed session
            const action = resolveCompletion(decided.completion);
            if (action.kind === "redirect")
              setPhase({
                name: "redirecting",
                url: action.url,
                ...(action.code ? { code: action.code } : {}),
              });
            else if (action.kind === "mturkPost")
              setPhase({
                name: "submitting",
                submitTo: action.submitTo,
                assignmentId: action.assignmentId,
              });
            else if (action.kind === "mturkError")
              setPhase({ name: "mturkError", assignmentId: action.assignmentId });
            else setPhase({ name: "ended", tone: "complete" });
            break;
          }
          case "return":
            clearResume(); // terminal (decline/abort): never resume
            if (decided.returnUrl) setPhase({ name: "redirecting", url: decided.returnUrl });
            else
              setPhase({
                name: "ended",
                tone: "aborted",
                endReason: decided.endReason ?? null,
                platform: paramsRef.current?.identity.source ?? "anonymous",
              });
            break;
          case "ended":
            clearResume(); // terminal: never resume
            setPhase({
              name: "ended",
              tone: "aborted",
              endReason: decided.endReason ?? null,
              platform: paramsRef.current?.identity.source ?? "anonymous",
            });
            break;
        }
        pendingSubmitRef.current = null; // submitted cleanly; nothing to retry.
      } catch (err) {
        if (err instanceof SessionApiError && err.code === "step_index_mismatch") {
          // 1e Part 1: the server already applied this result and advanced; the
          // response (or the original one) was lost. Don't show an error — silently
          // re-sync to the current step. The data is safe and the session moved on.
          pendingSubmitRef.current = null;
          await resync(handle);
          return;
        }
        // 1e Part 2: a genuine failure. Retain the result so "Try again" can
        // re-submit the SAME step_index, and show the retry-capable error screen.
        pendingSubmitRef.current = { stepIndex, type, result };
        setPhase({ name: "submitError", message: errorMessage(err) });
      }
    },
    [goRunning, resync],
  );

  const retrySubmit = useCallback(() => {
    const handle = handleRef.current;
    const pending = pendingSubmitRef.current;
    if (!handle || !pending) return;
    void runSubmit(handle, pending.stepIndex, pending.type, pending.result);
  }, [runSubmit]);

  const handleDone = useCallback(
    async (outcome: RendererOutcome) => {
      const handle = handleRef.current;
      const ctx = ctxRef.current;
      if (!handle || !ctx) return;
      if (outcome.kind === "advanced") {
        // The chat block advanced the session server-side; re-sync to the new step
        // (1d). The short poll hardens against a transient "pending" instead of
        // dead-ending, and resolves on attempt 1 in the normal case.
        setPhase({ name: "loading", message: "Saving..." });
        await resync(handle);
        return;
      }
      await runSubmit(handle, ctx.stepIndex, ctx.step.type, outcome.result);
    },
    [resync, runSubmit],
  );

  // The single online idle watcher (2a). It arms only while a run is on the
  // "running" phase (covering fresh begin, beginSandbox, and a future 2c resumed
  // run — all land on "running") and tears down on every transition away. onIdle
  // routes through FE-1's reason-aware end screen with end_reason "idle_timeout";
  // the watcher itself already told the server via heartbeat(handle, true).
  const onIdle = useCallback(() => {
    applyStepInterp({ kind: "ended", completed: false, aborted: true, endReason: "idle_timeout" });
  }, [applyStepInterp]);
  useIdleWatcher(phase.name === "running", handleRef.current, onIdle);

  // Tab-close beacon (2b): tell the server the participant left so it abandons the
  // session (reason "app_closed") and recycles the slot, instead of waiting for
  // the lazy idle sweep. Listener is attached ONLY while a real run is in progress
  // (phase === "running" and not a sandbox preview), so completion/redirect (which
  // have already left "running" before navigating) never beacon. pagehide, not
  // beforeunload (mobile-reliable, no prompt); a bfcache (persisted) hide is
  // skipped since the page may be restored.
  useEffect(() => {
    if (phase.name !== "running" || isSandbox) return;
    const onPageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return; // bfcache: may be restored — do not abandon
      const handle = handleRef.current;
      if (handle) sendCloseBeacon(handle);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [phase.name, isSandbox]);

  return (
    <Screen>
      {isSandbox ? <SandboxBanner /> : null}
      {renderPhase(phase, handleRef.current, handleDone, begin, redirectFailed, retrySubmit, beginSandboxPreview)}
    </Screen>
  );
}

// The preview/sandbox notice. Rendered as a fixed-position toast that fades
// in over the content and fades away (see .jol-preview-toast in tokens.css),
// rather than an in-flow bar that shifts the page layout when it appears or
// disappears. It unmounts itself once the fade-out finishes, so it shows once
// at the start of a preview run and then gets out of the way.
function SandboxBanner() {
  const [done, setDone] = useState(false);
  if (done) return null;
  return (
    <div
      className="jol-preview-toast"
      role="status"
      onAnimationEnd={() => setDone(true)}
    >
      Preview mode — this is a test run. Nothing here is recorded or counted.
    </div>
  );
}

// The hand-off screen while window.location.assign() takes the participant back
// to the platform. If that navigation is blocked or fails, this screen stays
// mounted — so when the backend gave us a completion code we surface it here as
// selectable text, the participant's only recourse to self-complete. The code is
// usually also embedded in the redirect url, so the happy path never needs it.
// `failed` is true only when assign() actually threw, in which case we drop the
// reassuring "Returning you..." copy for an explicit failure message.
export function Redirecting({ code, failed }: { code: string | null | undefined; failed: boolean }) {
  return (
    <Card>
      <div className="jol-center">
        {failed ? (
          <p className="jol-body">We could not redirect you automatically.</p>
        ) : (
          <>
            <p className="jol-body">Returning you to the study provider...</p>
            <div className="jol-spinner" role="status" aria-label="Loading" />
          </>
        )}
        {code ? (
          <p className="jol-body">
            {failed
              ? "Your completion code is:"
              : "If you are not redirected automatically, your completion code is:"}{" "}
            <strong style={{ userSelect: "all" }}>{code}</strong>
          </p>
        ) : null}
      </div>
    </Card>
  );
}

// The MTurk hand-off screen. submitMturkForm POSTs a form that navigates the page
// to the Worker site; on success this screen is replaced by that navigation. But
// a silent POST failure can't be detected, so we ALWAYS show the assignment id
// and the recourse text here, so a stuck worker still has a way to be paid.
export function Submitting({ assignmentId }: { assignmentId: string }) {
  return (
    <Card>
      <div className="jol-center">
        <p className="jol-body">Submitting your completion...</p>
        <div className="jol-spinner" role="status" aria-label="Loading" />
        <p className="jol-body">
          If this screen does not advance, your HIT was not submitted. Please contact the
          requester and do not close this window. Your assignment ID is:{" "}
          <strong style={{ userSelect: "all" }}>{assignmentId}</strong>
        </p>
      </div>
    </Card>
  );
}

// A malformed MTurk completion: the HIT could not be submitted at all. This must
// never read as success — show an explicit failure and the assignment id (when
// present) so the worker can ask the requester to approve the work manually.
export function MturkError({ assignmentId }: { assignmentId: string | null }) {
  return (
    <Card>
      <div className="jol-center">
        <h1 className="jol-title">We could not submit your HIT automatically</h1>
        <p className="jol-body">
          Your HIT was not submitted to MTurk automatically. Please contact the requester so
          they can approve your work, and do not close this window.
        </p>
        {assignmentId ? (
          <p className="jol-body">
            Your assignment ID is: <strong style={{ userSelect: "all" }}>{assignmentId}</strong>
          </p>
        ) : null}
      </div>
    </Card>
  );
}

// The non-completing end screen, shown only when there is NO automatic redirect
// (a present return_url redirects instead). It explains WHY the session ended and
// tells the participant how to hand themselves back to their platform. Copy and
// composition are fixed by the FE-1 contract (see abortScreen.fix.test.tsx).
export function AbortedScreen({
  endReason,
  platform,
}: {
  endReason: string | null;
  platform: IdentitySource;
}) {
  const isBudget = endReason === "budget_exhausted";
  const isIdle = endReason === "idle_timeout";
  const lead = isBudget
    ? "This session has ended early. Reason: token balance depleted. Please contact the researcher with any questions."
    : isIdle
      ? "This session ended because it was inactive for too long."
      : "This session has ended.";

  // Platform-specific recourse. sandbox (owner preview) and anything unknown fall
  // through to the anonymous default.
  let action: string | null;
  if (platform === "mturk")
    action =
      "Please return this HIT in your MTurk dashboard so it is not counted against you, then close this window.";
  else if (platform === "prolific") action = "You can now return to Prolific and close this window.";
  else if (platform === "sona") action = "You can now return to SONA and close this window.";
  else action = "Please wait for your researcher.";

  // budget + anonymous/other: the token line already says "contact the
  // researcher", so don't append the redundant "wait for your researcher" line.
  if (isBudget && action === "Please wait for your researcher.") action = null;

  return (
    <Card>
      <div className="jol-center">
        <p className="jol-body">{lead}</p>
        {action ? <p className="jol-body">{action}</p> : null}
      </div>
    </Card>
  );
}

// A retryable submit-failure screen: same "We hit a snag" copy as the static
// error, but with a "Try again" action that re-submits the retained result
// (mirrors the chat renderer's onRetry). Used only for submit failures, where the
// completed block's data would otherwise be unrecoverable.
export function SubmitError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card>
      <div className="jol-center">
        <h1 className="jol-title">We hit a snag</h1>
        <p className="jol-body">{message}</p>
        <div className="jol-row">
          <Button variant="primary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      </div>
    </Card>
  );
}

// Extracted from renderPhase so the dev-only preview catalog can render the
// REAL screens with no copy drift (single source of truth).
export function LandingScreen({
  onBegin,
  welcomeHtml,
  buttonLabel,
}: {
  onBegin: () => void;
  welcomeHtml?: string | undefined;
  buttonLabel?: string | undefined;
}) {
  return (
    <Card>
      {welcomeHtml ? (
        // Owner-authored welcome (rich text from the begin inspector), rendered
        // as HTML so the researcher's formatting shows. This path is the owner
        // PREVIEW of their own study; if it is ever reused for real participants,
        // sanitize the HTML first.
        <div className="jol-body" dangerouslySetInnerHTML={{ __html: welcomeHtml }} />
      ) : (
        <>
          <h1 className="jol-title">Welcome</h1>
          <p className="jol-body">
            Thank you for taking part. When you are ready, press Begin to start the study.
          </p>
        </>
      )}
      <div className="jol-row">
        <Button variant="primary" onClick={onBegin}>
          {buttonLabel || "Begin"}
        </Button>
      </div>
    </Card>
  );
}

export function PreviewNotice() {
  return (
    <Message
      title="Study preview"
      body="You are previewing this HIT. Accept it in MTurk to begin the study."
    />
  );
}

export function CompleteScreen() {
  return <CompleteScreen />;
}

export function SnagScreen({ message }: { message: string }) {
  return <Message title="We hit a snag" body={message} />;
}

function renderPhase(
  phase: Phase,
  session: SessionHandle | null,
  onDone: (o: RendererOutcome) => void,
  onBegin: () => void,
  redirectFailed: boolean,
  onRetrySubmit: () => void,
  onBeginSandbox: () => void,
) {
  switch (phase.name) {
    case "boot":
      return <Loading message="Loading..." />;
    case "landing":
      return (
        <LandingScreen
          onBegin={onBegin}
          welcomeHtml={phase.welcomeHtml}
          buttonLabel={phase.buttonLabel}
        />
      );
    case "previewLanding":
      return (
        <LandingScreen
          onBegin={onBeginSandbox}
          welcomeHtml={phase.welcomeHtml}
          buttonLabel={phase.buttonLabel}
        />
      );
    case "preview":
      return <PreviewNotice />;
    case "loading":
      return <Loading message={phase.message} />;
    case "redirecting":
      return <Redirecting code={phase.code} failed={redirectFailed} />;
    case "submitting":
      return <Submitting assignmentId={phase.assignmentId} />;
    case "mturkError":
      return <MturkError assignmentId={phase.assignmentId} />;
    case "ended":
      return phase.tone === "complete" ? (
        <Message title="All done" body="Thank you for taking part." />
      ) : (
        <AbortedScreen endReason={phase.endReason ?? null} platform={phase.platform ?? "anonymous"} />
      );
    case "submitError":
      return <SubmitError message={phase.message} onRetry={onRetrySubmit} />;
    case "error":
      return <SnagScreen message={phase.message} />;
    case "running": {
      const Renderer = RENDERERS[phase.step.type];
      if (Renderer && session) {
        return <Renderer step={phase.step} session={session} onDone={onDone} />;
      }
      return (
        <Message
          title={`Step: ${phase.step.type}`}
          body={`The "${phase.step.type}" screen is implemented in a later step.`}
        />
      );
    }
  }
}
